/**
 * New tournament on one page (#2691, F1 desktop, F24 phone).
 *
 * The organizer picks name, days, courts, events (matrix or quick start), the
 * fee and the registration window; the server sizes entry limits so it fits,
 * assigns days and runs the time check on every change (300 ms debounce).
 * Create writes everything in one transaction and lands on the Overview
 * checklist. Nothing is public until registration opens.
 */
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Drawer from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  createTournamentSetup,
  money,
  newIdempotencyKey,
  setupErrorMessage,
} from "@/libs/tournamentSetupApi";
import type {
  CourtConflictView,
  MatrixCell,
  QuickStart,
  SetupDay,
  SetupPreview,
  SetupPreviewRequest,
  StageDay,
} from "@/libs/tournamentSetupApi";
import { fromZonedDateTimeInput } from "@/libs/zonedDateTime";

import { CourtsPicker, useFacilityCourtsState } from "./setup/courts-picker";
import { DaysEditor } from "./setup/days-editor";
import { cellKey, EventsMatrix } from "./setup/events-matrix";
import type { SecondAxis } from "./setup/events-matrix";
import { addDays, formatDay, formatTime, PickerField } from "./setup/fields";
import { GlossaryProvider } from "./setup/glossary-drawer";
import { RegistrationWindowFields } from "./setup/registration-window-fields";
import type { RegistrationWindow } from "./setup/registration-window-fields";
import { TermHelp } from "./setup/term-help";
import { TimeCheckCard, TimeCheckRow } from "./setup/time-check-card";
import {
  CardBody,
  CheckIcon,
  DenseButton,
  DenseInput,
  DenseSelect,
  FieldLabel,
  Note,
  Segmented,
  SetupCard,
  SetupSurface,
  tone,
  WarningIcon,
} from "./setup/ui";
import { useSetupPreview } from "./setup/use-setup-preview";
import { WhatGetsCreated } from "./setup/what-gets-created";

const QUICK_STARTS: { value: QuickStart; label: string; phone: string }[] = [
  { value: "one_doubles", label: "One doubles event", phone: "One doubles" },
  { value: "club_standard", label: "Club standard", phone: "Club standard" },
  { value: "custom", label: "Custom", phone: "Custom" },
];
const QUICK_START_HELP: Record<QuickStart, string> = {
  one_doubles: "One doubles event: Mixed doubles · Open. Tick or untick any box to make it custom.",
  club_standard: "Club standard: all 5 disciplines at Open and B. Tick or untick any box to make it custom.",
  custom: "Custom: tick a box for each event you run.",
};
const LADDER = [8, 12, 16, 24, 32, 48, 64];
const SECOND_AXIS: Exclude<SecondAxis, null> = { ageGroups: ["U13", "U15", "U17"], levels: ["A", "B"] };

type Override = { cell: MatrixCell; entry_limit?: number | null; stage_days?: StageDay[] | null };

function nextWeekend(): string {
  const now = new Date();
  const day = now.getDay();
  const ahead = ((6 - day + 7) % 7) + 7; // the Saturday after next
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + ahead);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function tzShortLabel(timeZone: string): string {
  try {
    const name =
      new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value ?? "";
    return /^[A-Z][SD]T$/.test(name) ? `${name[0]}T` : name;
  } catch {
    return "";
  }
}

function moneyValue(text: string): string {
  const cleaned = text.replace(/[^0-9.]/g, "");
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) && value >= 0 ? value.toFixed(2) : "0.00";
}

function payloadKey(value: unknown): string {
  return JSON.stringify(value);
}

export function NewTournamentPage() {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { facility, facilityId } = useResolvedFacility();
  const facilityName = facility?.name ?? "your venue";
  // No silent fallback: a facility without a timezone can't schedule matches.
  const facilityTimeZone = (facility as { timezone?: string | null } | null)?.timezone ?? null;
  const timeZone = facilityTimeZone || "America/Los_Angeles";
  const tzLabel = tzShortLabel(timeZone);
  const { courts, failed: courtsFailed, retry: retryCourts } = useFacilityCourtsState(facilityId);

  const firstDay = useMemo(nextWeekend, []);
  const [name, setName] = useState("");
  const [fee, setFee] = useState("35.00");
  const [maxEvents, setMaxEvents] = useState<"3" | "2" | "any">("3");
  const [days, setDays] = useState<SetupDay[]>([
    { play_date: firstDay, first_match_at: "08:00:00", last_match_ends_at: "21:00:00", court_count: null },
    { play_date: addDays(firstDay, 1), first_match_at: "08:00:00", last_match_ends_at: "18:00:00", court_count: null },
  ]);
  const [courtIds, setCourtIds] = useState<string[] | null>(null);
  const [registration, setRegistration] = useState<RegistrationWindow>(() => ({
    opens: "on_open_click",
    openAt: null,
    closeAt: fromZonedDateTimeInput(`${addDays(firstDay, -8)}T23:59`, timeZone),
  }));
  const [quickStart, setQuickStart] = useState<QuickStart>("club_standard");
  const [cells, setCells] = useState<MatrixCell[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [secondAxis, setSecondAxis] = useState(false);
  const [editing, setEditing] = useState<"limits" | "days" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const attempt = useRef<{ key: string; idempotency: string } | null>(null);

  useEffect(() => {
    if (courts && courtIds === null) setCourtIds(courts.map((court) => court.court_id));
  }, [courts, courtIds]);

  const selectedCourts = courtIds ?? [];
  const limits = {
    max_events_per_player: maxEvents === "any" ? null : Number(maxEvents),
    one_level_per_discipline: true,
    max_age_groups_per_player: 2,
  };
  const draft: SetupPreviewRequest | null =
    days.length && (quickStart !== "custom" || cells.length) && selectedCourts.length
      ? {
          days,
          court_count: selectedCourts.length,
          quick_start: quickStart,
          cells: quickStart === "custom" ? cells : [],
          overrides: overrides.map((o) => ({ cell: o.cell, entry_limit: o.entry_limit ?? null, stage_days: o.stage_days ?? null, format: null })),
          entry_fee: money(moneyValue(fee)),
          currency: "usd",
          limits,
          registration_close_at: registration.closeAt,
          facility_court_ids: selectedCourts,
        }
      : null;
  const { data: preview, isFetching, error } = useSetupPreview(facilityId, draft);

  // A quick start's boxes come from the server's expansion, never a client copy.
  useEffect(() => {
    if (preview && quickStart !== "custom") setCells(preview.events.map((event) => event.cell));
  }, [preview, quickStart]);

  const toggleCell = (cell: MatrixCell) => {
    const key = cellKey(cell);
    const next = cells.some((c) => cellKey(c) === key) ? cells.filter((c) => cellKey(c) !== key) : [...cells, cell];
    setCells(next);
    setQuickStart("custom");
  };

  const chooseQuickStart = (value: QuickStart) => {
    setQuickStart(value);
    setOverrides([]);
  };

  const conflicts: CourtConflictView[] = preview?.court_conflicts ?? [];
  const timezoneMissing = Boolean(facility) && !facilityTimeZone;
  const canCreate = Boolean(
    draft !== null && preview && name.trim() && registration.closeAt && !isFetching && !submitting && !timezoneMissing,
  );

  const create = async () => {
    if (!canCreate || !preview || !facilityId || !registration.closeAt) return;
    const body = {
      name: name.trim(),
      days,
      facility_court_ids: selectedCourts,
      registration_opens: registration.opens,
      registration_open_at: registration.opens === "on_date" ? registration.openAt : null,
      registration_close_at: registration.closeAt,
      currency: "usd",
      limits,
      quick_start: quickStart,
      events: preview.events.map((event) => ({ ...event, entry_fee: money(moneyValue(fee)) })),
    };
    const key = payloadKey(body);
    if (!attempt.current || attempt.current.key !== key) attempt.current = { key, idempotency: newIdempotencyKey() };
    setSubmitting(true);
    setCreateError(null);
    try {
      const view = await createTournamentSetup(facilityId, { idempotency_key: attempt.current.idempotency, ...body });
      navigate(`/tournaments/${view.tournament_id}?tab=overview`);
    } catch (cause) {
      setCreateError(setupErrorMessage(cause, "The tournament couldn’t be created. Try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const shared = {
    preview, isFetching, error, name, setName, fee, setFee, maxEvents, setMaxEvents, days, setDays,
    courts: courts ?? [], courtIds: selectedCourts, setCourtIds, facilityName, timeZone, tzLabel, registration,
    setRegistration, quickStart, chooseQuickStart, cells, toggleCell, secondAxis, setSecondAxis, conflicts,
    canCreate, submitting, createError: timezoneMissing ? TIMEZONE_MISSING : createError, create, openEditor: setEditing,
    onCancel: () => navigate("/tournaments"),
    courtsFailed,
    retryCourts,
  };

  return (
    <GlossaryProvider>
      <SetupSurface sx={{ bgcolor: tone.page, minHeight: "100%", mt: { xs: -2, sm: -2.5 }, mb: { xs: -2, sm: -2.5 } }}>
        {phone ? <PhoneLayout {...shared} /> : <DesktopLayout {...shared} />}
        {preview ? (
          <OverrideEditor
            mode={editing}
            onClose={() => setEditing(null)}
            onSave={(next) => {
              setOverrides(next);
              setEditing(null);
            }}
            overrides={overrides}
            preview={preview}
            days={days}
          />
        ) : null}
      </SetupSurface>
    </GlossaryProvider>
  );
}

type LayoutProps = {
  preview: SetupPreview | undefined;
  isFetching: boolean;
  error: unknown;
  name: string;
  setName: (value: string) => void;
  fee: string;
  setFee: (value: string) => void;
  maxEvents: "3" | "2" | "any";
  setMaxEvents: (value: "3" | "2" | "any") => void;
  days: SetupDay[];
  setDays: (days: SetupDay[]) => void;
  courts: { court_id: string; name: string }[];
  courtIds: string[];
  setCourtIds: (ids: string[]) => void;
  facilityName: string;
  timeZone: string;
  tzLabel: string;
  registration: RegistrationWindow;
  setRegistration: (value: RegistrationWindow) => void;
  quickStart: QuickStart;
  chooseQuickStart: (value: QuickStart) => void;
  cells: MatrixCell[];
  toggleCell: (cell: MatrixCell) => void;
  secondAxis: boolean;
  setSecondAxis: (value: boolean) => void;
  conflicts: CourtConflictView[];
  canCreate: boolean;
  submitting: boolean;
  createError: string | null;
  create: () => void;
  openEditor: (mode: "limits" | "days") => void;
  onCancel: () => void;
  courtsFailed: boolean;
  retryCourts: () => void;
};

function CourtsError({ failed, retry }: { failed: boolean; retry: () => void }) {
  if (!failed) return null;
  return (
    <Note role="alert" sx={{ alignItems: "center" }} tone="red">
      <Box component="span" sx={{ flexGrow: 1 }}>
        The courts couldn’t load, so the time check can’t run yet.
      </Box>
      <DenseButton onClick={retry} size="sm">
        Retry
      </DenseButton>
    </Note>
  );
}

/** The tournament page's own message for a facility without a timezone. */
const TIMEZONE_MISSING = "Facility timezone unavailable. Match scheduling is unavailable.";

function FeeAndCap({ fee, setFee, maxEvents, setMaxEvents }: Pick<LayoutProps, "fee" | "setFee" | "maxEvents" | "setMaxEvents">) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
      <Box>
        <FieldLabel htmlFor="tfee">Entry fee per event</FieldLabel>
        <DenseInput id="tfee" inputMode="decimal" mono onChange={(event) => setFee(event.target.value)} value={fee.startsWith("$") ? fee : `$${fee}`} />
      </Box>
      <Box>
        <FieldLabel htmlFor="tmax">Players can enter up to</FieldLabel>
        <DenseSelect id="tmax" onChange={(event) => setMaxEvents(event.target.value as "3" | "2" | "any")} value={maxEvents}>
          <option value="3">3 events</option>
          <option value="2">2 events</option>
          <option value="any">Any number</option>
        </DenseSelect>
      </Box>
    </Box>
  );
}

function ConflictNote({ conflicts }: { conflicts: CourtConflictView[] }) {
  if (!conflicts.length) return null;
  return (
    <Note icon={<WarningIcon />} role="status" tone="amber" sx={{ alignItems: "flex-start" }}>
      <span>
        <b>
          {conflicts.length} existing booking{conflicts.length === 1 ? "" : "s"} in these hours stay:
        </b>{" "}
        {conflicts.slice(0, 3).map((c) => c.label).join("; ")}
        {conflicts.length > 3 ? `; and ${conflicts.length - 3} more` : ""}. The time check counts only free court time.
      </span>
    </Note>
  );
}

function LimitsNote({ preview, openEditor }: { preview: SetupPreview; openEditor: LayoutProps["openEditor"] }) {
  const fits = preview.time_check.all_fit;
  return (
    <Note icon={fits ? <CheckIcon /> : <WarningIcon />} tone={fits ? "green" : "amber"}>
      <span>
        <b>{fits ? "We set limits so it fits:" : "Limits:"}</b> {preview.limits_summary}.
      </span>
      <DenseButton onClick={() => openEditor("limits")} variant="link">
        Change
      </DenseButton>
      <Box sx={{ flexGrow: 1 }} />
      {preview.days_summary ? <span>{preview.days_summary}</span> : null}
      {preview.days_summary ? (
        <DenseButton onClick={() => openEditor("days")} variant="link">
          Change days
        </DenseButton>
      ) : null}
    </Note>
  );
}

function CreateError({ message }: { message: string | null }) {
  return message ? (
    <Note role="alert" tone="red">
      {message}
    </Note>
  ) : null;
}

function DesktopLayout(props: LayoutProps) {
  const { preview } = props;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 120px)" }}>
      <Box sx={{ height: 48, flex: "none", display: "flex", alignItems: "center", gap: 1, px: "20px", bgcolor: tone.surface, borderBottom: `1px solid ${tone.line}` }}>
        <Box component={RouterLink} sx={{ color: tone.subtle, fontWeight: 500, textDecoration: "none" }} to="/tournaments">
          Competitions
        </Box>
        <Box component="span" sx={{ color: tone.faint }}>
          ›
        </Box>
        <Box component="b" sx={{ fontSize: 14 }}>
          New tournament
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <DenseButton onClick={props.onCancel}>Cancel</DenseButton>
      </Box>
      <Box sx={{ flexGrow: 1, display: "flex", gap: 2, p: "16px 20px", alignItems: "flex-start" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.12fr)", gap: 1.5 }}>
            <SetupCard title="The basics">
              <CardBody>
                <Box>
                  <FieldLabel htmlFor="tname">Name</FieldLabel>
                  <DenseInput autoFocus id="tname" onChange={(event) => props.setName(event.target.value)} placeholder="Bay Area Fall Classic 2026" value={props.name} />
                </Box>
                <FeeAndCap {...props} />
                <Box sx={{ fontSize: 12, color: tone.subtle }}>
                  Doubles: {`$${moneyValue(props.fee).replace(/\.00$/, "")}`} per pair. Tech fee, if your plan has one, is added at checkout and shown to players before they pay.
                </Box>
              </CardBody>
            </SetupCard>
            <SetupCard title="Where and when">
              <CardBody>
                <DaysEditor
                  days={props.days}
                  onChange={props.setDays}
                  trailing={
                    <CourtsPicker courts={props.courts} facilityName={props.facilityName} onChange={props.setCourtIds} value={props.courtIds} />
                  }
                />
                <ConflictNote conflicts={props.conflicts} />
                <CourtsError failed={props.courtsFailed} retry={props.retryCourts} />
                <Box sx={{ pt: 1, borderTop: `1px solid ${tone.divider}` }}>
                  <RegistrationWindowFields
                    hint={preview?.registration_hint}
                    onChange={props.setRegistration}
                    timeZone={props.timeZone}
                    tzLabel={props.tzLabel}
                    value={props.registration}
                  />
                </Box>
              </CardBody>
            </SetupCard>
          </Box>
          <SetupCard
            actions={
              <DenseButton aria-pressed={props.secondAxis} onClick={() => props.setSecondAxis(!props.secondAxis)} size="sm">
                {props.secondAxis ? "Hide second axis" : "+ Second axis, like U15 B"}
              </DenseButton>
            }
            extra="Tick a box for each event you run"
            sx={{ display: "flex", flexDirection: "column" }}
            title="Events"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px 14px" }}>
              <FieldLabel sx={{ m: 0 }}>Quick start</FieldLabel>
              <Segmented label="Quick start" onChange={props.chooseQuickStart} options={QUICK_STARTS} value={props.quickStart} />
              <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
                {QUICK_START_HELP[props.quickStart]}
              </Box>
            </Box>
            <Box sx={{ overflowX: "auto", mb: "4px" }}>
              <EventsMatrix onToggle={props.toggleCell} secondAxis={props.secondAxis ? SECOND_AXIS : null} selected={props.cells} showCreates />
            </Box>
            <Box sx={{ m: "8px 14px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {preview ? <LimitsNote openEditor={props.openEditor} preview={preview} /> : null}
              {props.error ? <Note tone="red">{setupErrorMessage(props.error, "The time check couldn’t run. Check the days and events.")}</Note> : null}
              <Box sx={{ fontSize: 12, color: tone.subtle }}>
                Every event starts as Knockout + consolation <TermHelp label="About knockout + consolation" term="consolation" /> with at least 2 matches, entered as players and pairs. Change format, entry type or rules per event after you create it.
              </Box>
            </Box>
          </SetupCard>
        </Box>
        <Box sx={{ width: 340, flex: "none", display: "flex", flexDirection: "column", gap: 1.5, position: "sticky", top: 16 }}>
          {preview ? <WhatGetsCreated lines={preview.what_gets_created} /> : null}
          {preview ? <TimeCheckCard conflicts={props.conflicts} result={preview.time_check} /> : null}
          {!preview ? (
            <SetupCard title="Time check">
              <CardBody>
                <Box role="status" sx={{ fontSize: 12.5, color: tone.subtle }}>
                  {props.isFetching ? "Checking court time…" : "Add days, courts and at least one event to see whether it fits."}
                </Box>
              </CardBody>
            </SetupCard>
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          height: 56,
          flex: "none",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: "20px",
          bgcolor: tone.surface,
          borderTop: `1px solid ${tone.line}`,
          boxShadow: tone.raisedShadow,
          position: "sticky",
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
          Add later only if you need them: waiver, refunds, officials, sponsors, poster.
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {props.createError ? (
          <Box role="alert" sx={{ fontSize: 12.5, color: tone.badText, maxWidth: 420 }}>
            {props.createError}
          </Box>
        ) : (
          <Box component="span" sx={{ fontSize: 12.5, color: tone.muted }}>
            <b>Next:</b> a checklist, then Open registration. Nothing is public until then.
          </Box>
        )}
        <DenseButton disabled={!props.canCreate} onClick={props.create} size="lg" sx={{ width: 220 }} variant="primary">
          {props.submitting ? "Creating…" : "Create tournament"}
        </DenseButton>
      </Box>
    </Box>
  );
}

function PhoneSection({ children, title }: { children: React.ReactNode; title?: React.ReactNode }) {
  return (
    <Box component="section" sx={{ bgcolor: tone.surface, border: `1px solid ${tone.line}`, borderRadius: "10px", p: "10px 12px", display: "flex", flexDirection: "column", gap: 1 }}>
      {title ? (
        <Box component="h2" sx={{ m: 0, fontSize: 13, fontWeight: 700 }}>
          {title}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

function PhoneDayRow({ day, index, onChange, onRemove }: { day: SetupDay; index: number; onChange: (day: SetupDay) => void; onRemove?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ border: `1px solid ${tone.line}`, borderRadius: "8px" }}>
      <Box
        aria-expanded={open}
        component="button"
        onClick={() => setOpen(!open)}
        sx={{ width: "100%", minHeight: 44, display: "flex", alignItems: "center", px: "12px", bgcolor: tone.surface, border: 0, borderRadius: "8px", font: "inherit", cursor: "pointer" }}
        type="button"
      >
        <b>{formatDay(day.play_date)}</b>
        <Box sx={{ flexGrow: 1 }} />
        <Box component="span" sx={{ color: tone.muted, fontSize: 12.5 }}>
          {formatTime(day.first_match_at)} – {formatTime(day.last_match_ends_at)} ›
        </Box>
      </Box>
      {open ? (
        <Box sx={{ p: "0 12px 10px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
          <PickerField display={formatDay(day.play_date)} label={`Day ${index + 1} date`} onChange={(v) => onChange({ ...day, play_date: v })} type="date" value={day.play_date} />
          <PickerField display={formatTime(day.first_match_at)} label={`Day ${index + 1} first match`} onChange={(v) => onChange({ ...day, first_match_at: v })} type="time" value={day.first_match_at} />
          <PickerField display={formatTime(day.last_match_ends_at)} label={`Day ${index + 1} last match ends`} onChange={(v) => onChange({ ...day, last_match_ends_at: v })} type="time" value={day.last_match_ends_at} />
          {onRemove ? (
            <DenseButton onClick={onRemove} size="sm" sx={{ gridColumn: "1 / -1" }}>
              Remove this day
            </DenseButton>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function PhoneLayout(props: LayoutProps) {
  const { preview } = props;
  const [sheetOpen, setSheetOpen] = useState(false);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 56px)" }}>
      <Box sx={{ height: 52, flex: "none", display: "flex", alignItems: "center", gap: 1, px: "12px", bgcolor: tone.surface, borderBottom: `1px solid ${tone.line}` }}>
        <Box aria-label="Back to competitions" component={RouterLink} sx={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${tone.line}`, borderRadius: "6px", color: tone.ink2 }} to="/tournaments">
          <svg aria-hidden fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeWidth="2.4" viewBox="0 0 24 24" width="16">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </Box>
        <Box component="b" sx={{ fontSize: 15 }}>
          New tournament
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: "10px 12px", display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ fontSize: 12, color: tone.subtle }}>
          On a phone you start from the standard events. The full matrix, second axis and bulk edit are on desktop.
        </Box>
        <PhoneSection>
          <FieldLabel htmlFor="pn">Name</FieldLabel>
          <DenseInput id="pn" onChange={(event) => props.setName(event.target.value)} placeholder="Bay Area Fall Classic 2026" sx={{ height: 44, fontSize: 15 }} value={props.name} />
        </PhoneSection>
        <PhoneSection title="Days and courts">
          {props.days.map((day, index) => (
            <PhoneDayRow
              day={day}
              index={index}
              key={`${day.play_date}-${index}`}
              onChange={(next) => props.setDays(props.days.map((d, i) => (i === index ? next : d)).sort((a, b) => a.play_date.localeCompare(b.play_date)))}
              onRemove={props.days.length > 1 ? () => props.setDays(props.days.filter((_, i) => i !== index)) : undefined}
            />
          ))}
          <Box sx={{ display: "flex", gap: 1 }}>
            <DenseButton
              onClick={() => {
                const last = props.days[props.days.length - 1];
                props.setDays([...props.days, { ...last, play_date: addDays(last.play_date, 1) }]);
              }}
              size="lg"
              sx={{ flex: 1, height: 44 }}
            >
              + Add a day
            </DenseButton>
            <CourtsPicker courts={props.courts} facilityName={props.facilityName} onChange={props.setCourtIds} phone value={props.courtIds} />
          </Box>
          <ConflictNote conflicts={props.conflicts} />
          <CourtsError failed={props.courtsFailed} retry={props.retryCourts} />
        </PhoneSection>
        <PhoneSection
          title={
            <>
              Events{" "}
              <Box component="span" sx={{ fontWeight: 400, color: tone.subtle }}>
                {preview ? `${preview.events.length} will be created` : ""}
              </Box>
            </>
          }
        >
          <Segmented fill label="Quick start" size="phone" onChange={props.chooseQuickStart} options={QUICK_STARTS.map((q) => ({ value: q.value, label: q.phone }))} value={props.quickStart} />
          {preview ? (
            <>
              <Box sx={{ fontSize: 13, color: tone.ink2, lineHeight: 1.45 }}>
                {preview.events_line} <TermHelp label="What are levels?" term="level" />
              </Box>
              <Box sx={{ fontSize: 12.5, color: tone.muted }}>
                Entry type: <b>players and pairs</b> · knockout + consolation, at least 2 matches · limits sized to fit: {preview.limits_short}
              </Box>
            </>
          ) : props.quickStart === "custom" ? (
            <Box sx={{ fontSize: 12.5, color: tone.muted }}>Pick events one by one on a desktop, or start from a quick start.</Box>
          ) : null}
        </PhoneSection>
        <PhoneSection title="Registration">
          <RegistrationWindowFields hint={preview?.registration_hint} onChange={props.setRegistration} phone timeZone={props.timeZone} tzLabel={props.tzLabel} value={props.registration} />
        </PhoneSection>
        <PhoneSection>
          <FieldLabel htmlFor="pf">Entry fee per event</FieldLabel>
          <DenseInput id="pf" inputMode="decimal" mono onChange={(event) => props.setFee(event.target.value)} sx={{ height: 44 }} value={props.fee.startsWith("$") ? props.fee : `$${props.fee}`} />
        </PhoneSection>
      </Box>
      <Box sx={{ position: "sticky", bottom: 0, flex: "none", p: "8px 12px 16px", bgcolor: tone.surface, borderTop: `1px solid ${tone.line}`, display: "flex", flexDirection: "column", gap: "6px", zIndex: 2 }}>
        {preview ? <TimeCheckRow onOpen={() => setSheetOpen(true)} result={preview.time_check} /> : null}
        {props.error ? (
          <Note role="alert" tone="red">
            {setupErrorMessage(props.error, "The time check couldn’t run. Check the days and events.")}
          </Note>
        ) : null}
        <CreateError message={props.createError} />
        <DenseButton disabled={!props.canCreate} onClick={props.create} size="lg" sx={{ height: 48, fontSize: 15 }} variant="primary">
          {props.submitting ? "Creating…" : "Create tournament"}
        </DenseButton>
        <Box component="span" sx={{ fontSize: 12, color: tone.subtle, textAlign: "center" }}>
          Next: a checklist, then Open registration.
        </Box>
      </Box>
      {preview ? (
        <Drawer anchor="bottom" onClose={() => setSheetOpen(false)} open={sheetOpen} slotProps={{ paper: { sx: { borderRadius: "14px 14px 0 0", maxHeight: "85vh" } } }}>
          <SetupSurface sx={{ p: "12px", display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box component="b" sx={{ fontSize: 15, flexGrow: 1 }}>
                Time check
              </Box>
              <DenseButton onClick={() => setSheetOpen(false)} size="lg" sx={{ minWidth: 44 }}>
                Done
              </DenseButton>
            </Box>
            <TimeCheckCard conflicts={props.conflicts} result={preview.time_check} />
            {preview.time_check.suggestions.length ? (
              <Box>
                <Box sx={{ fontSize: 11.5, fontWeight: 700, color: tone.subtle, mb: "6px" }}>WAYS TO MAKE IT FIT, BEST FIRST</Box>
                {preview.time_check.suggestions.map((suggestion) => (
                  <Box key={suggestion.key} sx={{ p: "10px 12px", minHeight: 44, border: `1px solid ${tone.line}`, borderRadius: "8px", mb: "6px", fontSize: 13 }}>
                    <b>{suggestion.label}</b>
                    <Box sx={{ color: tone.muted, fontSize: 12 }}>
                      {suggestion.days.map((day) => `${day.label} ${day.percent_label}%`).join(" · ")} · {suggestion.outcome_label}
                    </Box>
                  </Box>
                ))}
                <Box sx={{ fontSize: 12, color: tone.subtle }}>Change limits or days on desktop, or after you create the tournament.</Box>
              </Box>
            ) : null}
          </SetupSurface>
        </Drawer>
      ) : null}
    </Box>
  );
}

/** Per-event limit or day overrides; each change re-previews. */
function OverrideEditor({
  mode,
  preview,
  overrides,
  days,
  onSave,
  onClose,
}: {
  mode: "limits" | "days" | null;
  preview: SetupPreview;
  overrides: Override[];
  days: SetupDay[];
  onSave: (next: Override[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Override[]>(overrides);
  useEffect(() => setDraft(overrides), [overrides, mode]);
  const valueFor = (cell: MatrixCell) => draft.find((o) => cellKey(o.cell) === cellKey(cell));
  const set = (cell: MatrixCell, patch: Partial<Override>) =>
    setDraft((current) => {
      const rest = current.filter((o) => cellKey(o.cell) !== cellKey(cell));
      return [...rest, { ...(valueFor(cell) ?? { cell }), ...patch }];
    });
  return (
    <Dialog onClose={onClose} open={mode !== null} slotProps={{ paper: { sx: { width: 520, borderRadius: "12px" } } }}>
      <SetupSurface>
        <Box sx={{ p: "14px 18px", borderBottom: `1px solid ${tone.divider}` }}>
          <Box component="b" sx={{ fontSize: 15 }}>
            {mode === "limits" ? "Entry limits" : "Days"}
          </Box>
          <Box sx={{ fontSize: 12, color: tone.subtle, mt: "2px" }}>
            {mode === "limits"
              ? "Set a limit for any event; the rest stay sized to fit. The time check updates as you go."
              : "Pick a day for any event; the rest keep their suggested day."}
          </Box>
        </Box>
        <Box sx={{ maxHeight: 420, overflowY: "auto", p: "4px 18px" }}>
          {preview.events.map((event, index) => {
            const current = valueFor(event.cell);
            return (
              <Box key={cellKey(event.cell)} sx={{ display: "flex", alignItems: "center", gap: 1, py: "6px", borderBottom: `1px solid ${tone.divider}` }}>
                <Box component="span" sx={{ flexGrow: 1, fontSize: 13, fontWeight: 600 }}>
                  {preview.event_names[index]}
                </Box>
                {mode === "limits" ? (
                  <DenseSelect
                    aria-label={`Entry limit for ${preview.event_names[index]}`}
                    onChange={(e) => set(event.cell, { entry_limit: e.target.value ? Number(e.target.value) : null })}
                    sx={{ width: 140 }}
                    value={current?.entry_limit ?? ""}
                  >
                    <option value="">Sized to fit ({event.entry_limit})</option>
                    {LADDER.map((size) => (
                      <option key={size} value={size}>
                        {size} {["MS", "WS"].includes(event.cell.kind) ? "players" : "pairs"}
                      </option>
                    ))}
                  </DenseSelect>
                ) : (
                  <DenseSelect
                    aria-label={`Day for ${preview.event_names[index]}`}
                    onChange={(e) =>
                      set(event.cell, { stage_days: e.target.value ? [{ stage: "all", play_date: e.target.value }] : null })
                    }
                    sx={{ width: 160 }}
                    value={current?.stage_days?.[0]?.play_date ?? ""}
                  >
                    <option value="">Suggested ({formatDay(event.stage_days[0].play_date)})</option>
                    {days.map((day) => (
                      <option key={day.play_date} value={day.play_date}>
                        {formatDay(day.play_date)}
                      </option>
                    ))}
                  </DenseSelect>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: 1, p: "12px 18px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
          <DenseButton onClick={() => setDraft([])} variant="link">
            Back to the suggestion
          </DenseButton>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton onClick={onClose}>Cancel</DenseButton>
          <DenseButton
            onClick={() => onSave(draft.filter((o) => (mode === "limits" ? o.entry_limit : o.stage_days) || o.entry_limit || o.stage_days))}
            variant="primary"
          >
            Apply
          </DenseButton>
        </Box>
      </SetupSurface>
    </Dialog>
  );
}

